import { can } from '@simplificalo/contracts/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { Building, Lock, Save } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganization, useUpdateOrganization, type Organization } from '@/lib/organization';
import { useCurrentUserRole } from '@/lib/team';

export const Route = createFileRoute('/_app/settings/organization')({
  component: OrganizationPage,
});

function OrganizationPage() {
  const role = useCurrentUserRole();
  const { data: org, isLoading, error } = useOrganization();
  const canEdit = can(role, 'organization.edit');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Organización</h1>
        <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
          Datos comerciales y de contacto de la empresa.
          {!canEdit ? (
            <>
              {' '}
              <span className="inline-flex items-center gap-1 rounded-md bg-[color:var(--color-bg-overlay)] px-1.5 py-0.5 text-xs">
                <Lock className="h-3 w-3" aria-hidden /> Solo lectura — únicamente owner edita
              </span>
            </>
          ) : null}
        </p>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : error ? (
        <div
          className="rounded-lg border px-3 py-2 text-xs"
          style={{
            background: 'color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--color-danger, #ef4444) 30%, transparent)',
          }}
        >
          Error al cargar organización: {error.message}
        </div>
      ) : !org ? null : (
        <OrgForm org={org} canEdit={canEdit} />
      )}
    </div>
  );
}

interface OrgFormProps {
  readonly org: Organization;
  readonly canEdit: boolean;
}

function OrgForm({ org, canEdit }: OrgFormProps) {
  const update = useUpdateOrganization();

  const [name, setName] = useState(org.name);
  const [rut, setRut] = useState(org.rut ?? '');
  const [address, setAddress] = useState(org.address ?? '');
  const [comuna, setComuna] = useState(org.comuna ?? '');
  const [ciudad, setCiudad] = useState(org.ciudad ?? '');
  const [phone, setPhone] = useState(org.phone ?? '');
  const [contactEmail, setContactEmail] = useState(org.contactEmail ?? '');

  // Re-sync local state cuando cambia el org cargado (post-save).
  useEffect(() => {
    setName(org.name);
    setRut(org.rut ?? '');
    setAddress(org.address ?? '');
    setComuna(org.comuna ?? '');
    setCiudad(org.ciudad ?? '');
    setPhone(org.phone ?? '');
    setContactEmail(org.contactEmail ?? '');
  }, [org]);

  const handle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canEdit) return;
    update.mutate(
      {
        name: name.trim(),
        rut: rut.trim() || null,
        address: address.trim() || null,
        comuna: comuna.trim() || null,
        ciudad: ciudad.trim() || null,
        phone: phone.trim() || null,
        contactEmail: contactEmail.trim() || null,
      },
      {
        onSuccess: () => toast.success('Organización actualizada'),
        onError: (err) => toast.error(`Error al guardar: ${err.message}`),
      },
    );
  };

  return (
    <form onSubmit={handle} className="space-y-6">
      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Identidad legal</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Razón social" htmlFor="org-name" required>
            <Input
              id="org-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="RUT" htmlFor="org-rut">
            <Input
              id="org-rut"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              placeholder="76123456-7"
              disabled={!canEdit}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Dirección</h2>
        <Field label="Dirección" htmlFor="org-address">
          <Input
            id="org-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={!canEdit}
            placeholder="Calle Ejemplo 123, oficina 45"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Comuna" htmlFor="org-comuna">
            <Input
              id="org-comuna"
              value={comuna}
              onChange={(e) => setComuna(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Ciudad" htmlFor="org-ciudad">
            <Input
              id="org-ciudad"
              value={ciudad}
              onChange={(e) => setCiudad(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Contacto</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Teléfono" htmlFor="org-phone">
            <Input
              id="org-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!canEdit}
              placeholder="+56 9 1234 5678"
            />
          </Field>
          <Field label="Email de contacto" htmlFor="org-email">
            <Input
              id="org-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              disabled={!canEdit}
              placeholder="contacto@empresa.cl"
            />
          </Field>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold">Logo</h2>
        <div
          className="flex items-center gap-4 rounded-lg border p-4"
          style={{
            background: 'var(--color-bg-elevated)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md"
            style={{ background: 'var(--color-bg-overlay)' }}
          >
            <Building className="h-7 w-7 text-[color:var(--color-fg-subtle)]" aria-hidden />
          </div>
          <div className="text-xs text-[color:var(--color-fg-muted)]">
            <strong>Próximamente foto upload.</strong> Por ahora podés pegar la
            URL del logo (ej. CDN propio) en la columna logo_url via el
            backend cuando se habilite.
          </div>
        </div>
      </section>

      {canEdit ? (
        <div className="flex justify-end gap-2 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
          <Button type="submit" disabled={update.isPending}>
            <Save className="mr-1.5 h-4 w-4" aria-hidden />
            {update.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

interface FieldProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly required?: boolean;
  readonly children: React.ReactNode;
}

function Field({ label, htmlFor, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <span className="text-[color:var(--color-fg-subtle)]"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
